import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, safeChannel } from "./supabase";

// useKidColoringPages — list of every coloring page a kid has
// generated. Returns rows in created_at desc order so the UI can
// render a "most recent first" grid. Realtime-subscribed so a fresh
// page from kid-color-craft appears live without a reload.
//
// Returned shape (per row):
//   { id, kid_user_id, combo_name, combo_a_emoji, combo_b_emoji,
//     pet_id, line_art_url, colored_url, colored_at, created_at }

export function useKidColoringPages(kidId) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef(null);

  const load = useCallback(async () => {
    if (!kidId) { setPages([]); setLoading(false); return; }
    setLoading(true);
    const { data, error } = await supabase
      .from("kid_coloring_pages")
      .select("id, kid_user_id, combo_name, combo_a_emoji, combo_b_emoji, pet_id, line_art_url, line_art_svg_url, source_image_url, colored_url, colored_state, colored_at, created_at")
      .eq("kid_user_id", kidId)
      .order("created_at", { ascending: false });
    if (error) console.warn("[kid-coloring-pages] load:", error);
    setPages(data || []);
    setLoading(false);
  }, [kidId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!kidId) return;
    const ch = safeChannel(`rt:kid_coloring_pages:${kidId}`)
      .on("postgres_changes",
        { event: "*", schema: "public", table: "kid_coloring_pages", filter: `kid_user_id=eq.${kidId}` },
        () => { load(); })
      .subscribe();
    channelRef.current = ch;
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [kidId, load]);

  return { pages, loading, refresh: load };
}
